import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, withModifiers as _withModifiers, createBlock as _createBlock, TransitionGroup as _TransitionGroup, withCtx as _withCtx, createVNode as _createVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "coin-select__header" }
const _hoisted_2 = { class: "coin-select__header-left" }
const _hoisted_3 = {
  key: 0,
  class: "coin-select__header-right"
}

import { values, throttle } from 'lodash/fp';
import { computed, onMounted, ref, watch, type PropType } from 'vue';

import BigNumber from '@exchange/helpers/bignumber';
import { type BalanceModel } from '@exchange/libs/balances/service/src';
import { modalVariant, type ModalVariant } from '@exchange/libs/modals/src';
import { type CurrencyModel } from '@exchange/libs/utils/currency/src';

import CoinSelectItem from './CoinSelectItem.vue';

type Dialog = (typeof DialogType)[keyof typeof DialogType];

interface BalanceCurrencyPair {
  currency: CurrencyModel;
  balance: BigNumber;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'CoinSelect',
  props: {
  balances: { type: Array as PropType<Array<BalanceModel>> },
  currencies: { type: Array as PropType<Array<CurrencyModel>> },
  defaultFiatCurrency: { type: String, required: true },
  selectedCurrency: { type: Object as PropType<CurrencyModel> },
  type: { type: String as PropType<Dialog>, required: true },
  variant: { type: String as PropType<ModalVariant>, default: modalVariant.light },
},
  emits: ["update:selectedCurrency"],
  setup(__props, { emit: __emit }) {

const DialogType = {
  DEPOSIT: 'deposit',
  WITHDRAW: 'withdraw',
  TRANSFER: 'transfer',
} as const;

const props = __props;

const emit = __emit;

const coinSelectEl = ref<HTMLElement>();
const contentEl = ref();

const isWithdraw = computed(() => props.type === DialogType.WITHDRAW);
const isTransfer = computed(() => props.type === DialogType.TRANSFER);

const throttledBalanceToUse = ref(props.balances);

const getFiatValue = (pair: BalanceCurrencyPair) => pair.balance.times(pair.currency.defaultFiatValue || 0);
const sortByCurrencyName = (a: BalanceCurrencyPair, b: BalanceCurrencyPair) => {
  if (!a.currency.name && b.currency.name) {
    return 1;
  }

  if (!b.currency.name && a.currency.name) {
    return -1;
  }

  if (!a.currency.name || !b.currency.name) {
    return a.currency.id.localeCompare(b.currency.id);
  }

  return a.currency.name.localeCompare(b.currency.name);
};
const sortByBalance = (a: BalanceCurrencyPair, b: BalanceCurrencyPair) => b.balance.minus(a.balance).toNumber();
const sortByFiatValue = (a: BalanceCurrencyPair, b: BalanceCurrencyPair) => getFiatValue(b).minus(getFiatValue(a)).toNumber();
const sortByPreselectedCurrency = (selectedCurrencyId: string | undefined) => (a: BalanceCurrencyPair, b: BalanceCurrencyPair) => {
  if (selectedCurrencyId) {
    if (selectedCurrencyId === a.currency.id) {
      return -1;
    }

    if (selectedCurrencyId === b.currency.id) {
      return 1;
    }
  }

  return 0;
};

const updateThrottledBalance = (balances: Array<BalanceModel> | undefined) => {
  throttledBalanceToUse.value = balances;
};

const currencyBalancePairs = computed(() => {
  const pairs: Array<BalanceCurrencyPair> = values(props.currencies)
    .map((currency: CurrencyModel) => {
      const currentBalances = throttledBalanceToUse.value;

      const findByCurrencyId = <T extends { currencyCode: string }>(arr: Array<T>) => arr.find((e) => currency.id === e.currencyCode);

      if (currentBalances) {
        const cb: Array<(typeof currentBalances)[number]> = currentBalances;
        const balance = findByCurrencyId(cb);

        if (balance) {
          const total = balance.getTotal();
          const balanceIsTooSmall = currency.checkAmountIsTooSmall(total);

          return {
            currency,
            balance: balanceIsTooSmall ? new BigNumber(0) : total,
            shown: true,
          };
        }
      }

      return {
        currency,
        balance: new BigNumber(0),
        shown: true,
      };
    })
    .filter((p) => p.shown);

  pairs.sort(sortByCurrencyName);

  if (isWithdraw.value || isTransfer.value) {
    pairs.sort(sortByBalance);
    pairs.sort(sortByFiatValue);
  }

  return pairs.sort(sortByPreselectedCurrency(props.selectedCurrency?.id));
});

const onItemClick = (pair: BalanceCurrencyPair) => {
  emit('update:selectedCurrency', props.selectedCurrency ? undefined : pair.currency);
};

watch(
  () => props.balances,
  throttle(60_000, (newVal) => updateThrottledBalance(newVal)),
);

onMounted(() => {
  if (!coinSelectEl.value || !contentEl.value) {
    return;
  }

  if (props.selectedCurrency) {
    if (contentEl.value) {
      contentEl.value.scrollTop = 0;
    }
  }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "coinSelectEl",
    ref: coinSelectEl,
    class: _normalizeClass(["coin-select", [`coin-select--${__props.variant}`]])
  }, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, _toDisplayString(_ctx.$t('modules.transactions.currencySelector.headline.selectCurrency')), 1),
      (isWithdraw.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, _toDisplayString(_ctx.$t('modules.transactions.currencySelector.headline.amountAndFiatValue', { fiat: __props.defaultFiatCurrency })), 1))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("div", {
      ref_key: "contentEl",
      ref: contentEl,
      class: _normalizeClass(["coin-select__content", { collapsed: !!__props.selectedCurrency, single: currencyBalancePairs.value.length === 1 }])
    }, [
      _createVNode(_TransitionGroup, {
        name: "coin-list",
        appear: true
      }, {
        default: _withCtx(() => [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(currencyBalancePairs.value, (pair) => {
            return (_openBlock(), _createBlock(CoinSelectItem, {
              key: pair.currency.id,
              class: "coin-select__item",
              "is-selected": __props.selectedCurrency === pair.currency,
              "is-withdraw": isWithdraw.value,
              variant: __props.variant,
              currency: pair.currency,
              balance: pair.balance,
              onClick: _withModifiers(($event: any) => (onItemClick(pair)), ["stop"])
            }, null, 8, ["is-selected", "is-withdraw", "variant", "currency", "balance", "onClick"]))
          }), 128))
        ]),
        _: 1
      })
    ], 2)
  ], 2))
}
}

})